import { jsx } from "react/jsx-runtime";
import { getLocalTimeZone, isSameMonth, isSameYear } from "@internationalized/date";
import { useDateFormatter } from "@react-aria/i18n";
import { useMemo } from "react";
import "../ThemeProvider/ThemeProvider.mjs";
import "../ThemePortal/ThemePortal.mjs";
import "../Text/TextContext.mjs";
import "../ThemeProvider/ThemeContext.mjs";
import "../ThemeProvider/VanillaThemeContainer.mjs";
import "../private/DefaultMessagesContext.mjs";
import "../ThemeProvider/BreakpointContext.mjs";
import "../../css/atoms/sprinkles.css.mjs";
import { PublicBox } from "../Box/Box.mjs";
import "../Box/BackgroundContext.mjs";
import "../Divider/Divider.css.mjs";
import "../Heading/HeadingContext.mjs";
import "../Hidden/Hidden.css.mjs";
import "../../css/negativeMargin/negativeMargin.css.mjs";
import "../Accordion/AccordionContext.mjs";
import { Text } from "../Text/Text.mjs";
import "../Columns/ColumnsContext.mjs";
import "../Column/Column.css.mjs";
import "../../css/lineHeightContainer.css.mjs";
import "../../css/reset/reset.css.mjs";
import "../../hooks/useIcon/icon.css.mjs";
import "../../css/typography.css.mjs";
import "../private/touchable/virtualTouchable.css.mjs";
import "../private/hideFocusRings/hideFocusRings.css.mjs";
import "../Accordion/AccordionItem.css.mjs";
import "../Actions/ActionsContext.mjs";
import "../../css/textAlignedToIcon.css.mjs";
import "../Alert/Alert.css.mjs";
import "../Autosuggest/Autosuggest.mjs";
import "../Avatar/Avatar.css.mjs";
import "../Badge/Badge.mjs";
import "../Breadcrumb/Breadcrumb.css.mjs";
import "../Box/ColoredBox.mjs";
import "../Button/Button.mjs";
import "../ButtonIcon/ButtonIcon.mjs";
import "../ButtonLink/ButtonLink.mjs";
import "../private/Keyline/Keyline.css.mjs";
import "../Checkbox/Checkbox.mjs";
import "../Checkbox/CheckboxStandalone.mjs";
import "../ContentBlock/ContentBlock.css.mjs";
import "./DateField.mjs";
import "../private/Field/FloatingLabel.css.mjs";
import "../private/Field/Field.css.mjs";
import { selector } from "./DateField.css.mjs";
import "../private/Modal/Modal.mjs";
import "../private/Modal/ModalContent.mjs";
import "../private/Modal/ModalContext.mjs";
import "../TextLink/TextLink.mjs";
import "../TextLinkButton/TextLinkButton.css.mjs";
import "../private/AvoidWidowIcon/AvoidWidowIcon.css.mjs";
import "../Dropdown/Dropdown.mjs";
import "../private/MaxLines/MaxLines.css.mjs";
import "../HiddenVisually/HiddenVisually.css.mjs";
import "../Link/Link.mjs";
import "../private/defaultTextProps.mjs";
import "../List/List.css.mjs";
import "../Loader/Loader.css.mjs";
import "../Skeleton/Skeleton.css.mjs";
import { MenuRenderer } from "../MenuRenderer/MenuRenderer.mjs";
import { MenuItem } from "../MenuItem/MenuItem.mjs";
import "../MenuItemCheckbox/MenuItemCheckbox.mjs";
import "../MenuItemDivider/MenuItemDivider.mjs";
import "../MenuItem/MenuItemLink.mjs";
import "../OverflowMenu/OverflowMenu.css.mjs";
import "../MonthPicker/MonthPicker.mjs";
import "../Pagination/Pagination.css.mjs";
import "../PasswordField/PasswordField.mjs";
import "../Pincode/Pincode.mjs";
import "../PopoverRenderer/PopoverRenderer.mjs";
import "../Pressable/Pressable.mjs";
import "../RadioGroup/RadioGroup.mjs";
import "../RadioGroup/RadioItem.mjs";
import "../RadioGroup/RadioItemBlock.mjs";
import "../SelectableList/SelectableList.css.mjs";
import "../private/BulkActions/BulkActions.css.mjs";
import "../Spread/Spread.css.mjs";
import "../Stepper/Step.mjs";
import "../Stepper/Stepper.mjs";
import "../Tabs/Tab.mjs";
import "../Table/Table.mjs";
import "../Table/Table.css.mjs";
import "../Tabs/TabsProvider.mjs";
import "../Tabs/TabListContext.mjs";
import "../Tabs/Tabs.css.mjs";
import "../Tabs/TabPanelsContext.mjs";
import "../Tag/Tag.css.mjs";
import "../Textarea/Textarea.mjs";
import "../TextDropdown/TextDropdown.css.mjs";
import "../TextField/TextField.mjs";
import "../Tiles/Tiles.css.mjs";
import "../Toggle/Toggle.mjs";
import "../useToast/ToastContext.mjs";
import "../TooltipRenderer/TooltipRenderer.mjs";
import "../TimeField/DateSegment.css.mjs";
import "../private/FieldButtonIcon/FieldButtonIcon.mjs";
import "../MultiStepModal/MultiStepModal.mjs";
import "../SearchField/SearchField.mjs";
import "../RadioBar/RadioBarContext.mjs";
import "../RadioBar/RadioBar.css.mjs";
import "../../themes/vars.css.mjs";
import "../RadioBar/RadioBar.mjs";
function getYears(activeDate, minDate, maxDate) {
  const firstYear = activeDate.set({ year: activeDate.year - 100 });
  return Array(200).fill(0).map((_, diff) => firstYear.add({ years: diff })).filter(
    (year) => (minDate === void 0 || minDate === null || year.year >= (minDate == null ? void 0 : minDate.year)) && (maxDate === void 0 || maxDate === null || year.year <= (maxDate == null ? void 0 : maxDate.year))
  );
}
function getMonths(activeDate) {
  return Array(12).fill(0).map((_, month) => activeDate.set({ month: month + 1 }));
}
function Selector({ datePart, activeDate, maxDate, minDate, onSelect, width }) {
  const formatter = useDateFormatter(datePart === "month" ? { month: "long" } : { year: "numeric" });
  const values = datePart === "month" ? getMonths(activeDate) : getYears(activeDate, minDate, maxDate);
  const options = useMemo(
    () => values.map((value) => ({
      label: formatter.format(value.toDate(getLocalTimeZone())),
      onPress: () => onSelect(value),
      isSelected: isSameMonth(value, activeDate) && isSameYear(value, activeDate)
    })),
    [values, activeDate, onSelect, formatter]
  );
  return /* @__PURE__ */ jsx(
    MenuRenderer,
    {
      width,
      trigger: (triggerProps) => /* @__PURE__ */ jsx(
        PublicBox,
        {
          ...triggerProps,
          cursor: "pointer",
          outline: "none",
          className: selector,
          "aria-label": datePart,
          children: /* @__PURE__ */ jsx(Text, { size: "small", children: formatter.format(activeDate.toDate(getLocalTimeZone())) })
        }
      ),
      shouldFlip: false,
      children: options.map((option, i) => /* @__PURE__ */ jsx(MenuItem, { onClick: option.onPress, data: { calendar: "selector" }, children: option.label }, i))
    }
  );
}
export {
  Selector
};
